import { useState, useMemo } from "react";
import { usePage, Link } from "@inertiajs/react";
import { Head } from "@inertiajs/react";
import DashboardLayout from "../../../components/DashboardLayout";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from "recharts";
import { 
  BarChart3, 
  Calendar, 
  Globe, 
  Monitor, 
  Smartphone, 
  Tablet, 
  Link as LinkIcon,
  ArrowUpRight,
  Users,
  Eye,
  ExternalLink
} from "lucide-react";

interface AnalyticsProps {
  visitsOverTime: Record<string, number>;
  topPages: Record<string, number>;
  referrers: Record<string, number>;
  devices: Record<string, number>;
  browsers: Record<string, number>;
  countries: Record<string, number>;
  since: string;
  till: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

function getDeviceIcon(device: string): React.ReactNode {
  const lower = device.toLowerCase();
  if (lower.includes('mobile')) return <Smartphone className="h-4 w-4" />;
  if (lower.includes('tablet')) return <Tablet className="h-4 w-4" />;
  return <Monitor className="h-4 w-4" />;
}

function getDeviceColor(device: string): string {
  const lower = device.toLowerCase();
  if (lower.includes('mobile')) return 'bg-blue-500 dark:bg-blue-600';
  if (lower.includes('tablet')) return 'bg-amber-500 dark:bg-amber-600';
  return 'bg-green-500 dark:bg-green-600';
}

const Analytics: React.FC = () => {
  const pageProps = usePage().props as unknown as AnalyticsProps;
  
  const [dateRange, setDateRange] = useState({
    since: pageProps.since || (new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]),
    till: pageProps.till || new Date().toISOString().split('T')[0]
  });

  // Transform visits over time for chart
  const visitsChartData = useMemo(() => {
    return Object.entries(pageProps.visitsOverTime)
      .map(([date, count]) => ({ date, visits: count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [pageProps.visitsOverTime]);

  // Transform top pages for chart
  const topPagesData = useMemo(() => {
    return Object.entries(pageProps.topPages)
      .map(([url, views]) => ({ 
        url: url.length > 40 ? url.substring(0, 40) + '...' : url, 
        fullUrl: url,
        views 
      }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 8);
  }, [pageProps.topPages]);

  // Transform referrers for chart
  const referrersData = useMemo(() => {
    return Object.entries(pageProps.referrers)
      .map(([referrer, visits]) => ({ referrer, visits }))
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 8);
  }, [pageProps.referrers]);

  // Transform devices for pie chart
  const devicesData = useMemo(() => {
    return Object.entries(pageProps.devices)
      .map(([name, value]) => ({ name: name || 'Unknown', value }))
      .sort((a, b) => b.value - a.value);
  }, [pageProps.devices]);

  // Transform browsers for pie chart
  const browsersData = useMemo(() => {
    return Object.entries(pageProps.browsers)
      .map(([name, value]) => ({ name: name || 'Unknown', value }))
      .sort((a, b) => b.value - a.value);
  }, [pageProps.browsers]);

  // Transform countries for pie chart
  const countriesData = useMemo(() => {
    return Object.entries(pageProps.countries)
      .map(([name, value]) => ({ name: name || 'Unknown', value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [pageProps.countries]);

  // Calculate totals
  const totalVisits = useMemo(() => {
    return Object.values(pageProps.visitsOverTime).reduce((sum, val) => sum + val, 0);
  }, [pageProps.visitsOverTime]);

  const totalPageViews = useMemo(() => {
    return Object.values(pageProps.topPages).reduce((sum, val) => sum + val, 0);
  }, [pageProps.topPages]);

  const handleDateChange = () => {
    // Use Inertia to navigate with new date range
    window.location.href = `/admin/analytics?since=${dateRange.since}&till=${dateRange.till}`;
  };

  const formatUrl = (url: string): string => {
    try {
      const parsed = new URL(url);
      return parsed.pathname || '/';
    } catch {
      return url.length > 30 ? url.substring(0, 30) + '...' : url;
    }
  };

  return (
    <DashboardLayout>
      <Head title="Analytics" />

      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <BarChart3 className="h-6 w-6" />
              Analytics
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Track your website traffic and user behavior.
            </p>
          </div>
          
          {/* Date Range Picker */}
          <div className="flex items-center gap-2 bg-card dark:bg-card rounded-lg p-2 border">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <input 
              type="date" 
              value={dateRange.since} 
              onChange={(e) => setDateRange({ ...dateRange, since: e.target.value })} 
              className="bg-transparent border-none text-sm text-foreground focus:outline-none dark:text-gray-100"
            />
            <span className="text-muted-foreground">to</span>
            <input 
              type="date" 
              value={dateRange.till} 
              onChange={(e) => setDateRange({ ...dateRange, till: e.target.value })} 
              className="bg-transparent border-none text-sm text-foreground focus:outline-none dark:text-gray-100"
            />
            <Button size="sm" onClick={handleDateChange}>
              Apply
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="rounded-xl border bg-card text-card-foreground shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Visits</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalVisits.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                In selected date range
              </p>
            </CardContent>
          </Card>
          
          <Card className="rounded-xl border bg-card text-card-foreground shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Page Views</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalPageViews.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                Total page views
              </p>
            </CardContent>
          </Card>
          
          <Card className="rounded-xl border bg-card text-card-foreground shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unique Pages</CardTitle>
              <LinkIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Object.keys(pageProps.topPages).length}</div>
              <p className="text-xs text-muted-foreground">
                Pages visited
              </p>
            </CardContent>
          </Card>
          
          <Card className="rounded-xl border bg-card text-card-foreground shadow">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Referrers</CardTitle>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Object.keys(pageProps.referrers).length}</div>
              <p className="text-xs text-muted-foreground">
                Traffic sources
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Visits Over Time Chart */}
        <Card className="rounded-xl border bg-card text-card-foreground shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Visits Over Time
            </CardTitle>
            <CardDescription>
              Daily visit trends for the selected date range.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {visitsChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={visitsChartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                    className="text-muted-foreground"
                  />
                  <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--card)', 
                      border: '1px solid var(--border)',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="visits" 
                    stroke="#8884d8" 
                    strokeWidth={2}
                    dot={{ fill: '#8884d8', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                    name="Visits"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                No visit data available for the selected date range.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Pages and Referrers */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Top Pages */}
          <Card className="rounded-xl border bg-card text-card-foreground shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LinkIcon className="h-5 w-5" />
                Top Pages
              </CardTitle>
              <CardDescription>
                Most visited pages in the selected period.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {topPagesData.length > 0 ? (
                <div className="space-y-3">
                  {topPagesData.map((page, index) => (
                    <div 
                      key={page.fullUrl} 
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                          {index + 1}
                        </div>
                        <span className="text-sm font-medium truncate text-foreground" title={page.fullUrl}>
                          {formatUrl(page.url)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Eye className="h-3 w-3" />
                        <span className="font-semibold">{page.views.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No page view data available.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Referrers */}
          <Card className="rounded-xl border bg-card text-card-foreground shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowUpRight className="h-5 w-5" />
                Top Referrers
              </CardTitle>
              <CardDescription>
                Where your visitors are coming from.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {referrersData.length > 0 ? (
                <div className="space-y-3">
                  {referrersData.map((ref, index) => (
                    <div 
                      key={ref.referrer} 
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                          {index + 1}
                        </div>
                        <span className="text-sm font-medium truncate text-foreground" title={ref.referrer}>
                          {formatUrl(ref.referrer)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-3 w-3" />
                        <span className="font-semibold">{ref.visits.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No referrer data available.
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Device, Browser, and Country Breakdown */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Devices */}
          <Card className="rounded-xl border bg-card text-card-foreground shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="h-5 w-5" />
                Devices
              </CardTitle>
              <CardDescription>
                Device types used by visitors.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {devicesData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={devicesData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                      >
                        {devicesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2">
                    {devicesData.map((device, index) => (
                      <div key={device.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${getDeviceColor(device.name)}`} />
                          <span className="text-sm text-foreground">{device.name}</span>
                        </div>
                        <span className="text-sm font-medium text-muted-foreground">
                          {device.value} ({Math.round((device.value / totalVisits) * 100)}%)
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No device data available.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Browsers */}
          <Card className="rounded-xl border bg-card text-card-foreground shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Browsers
              </CardTitle>
              <CardDescription>
                Browser distribution of visitors.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {browsersData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={browsersData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                      >
                        {browsersData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2">
                    {browsersData.slice(0, 5).map((browser, index) => (
                      <div key={browser.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                          <span className="text-sm text-foreground">{browser.name}</span>
                        </div>
                        <span className="text-sm font-medium text-muted-foreground">
                          {browser.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No browser data available.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Countries */}
          <Card className="rounded-xl border bg-card text-card-foreground shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Countries
              </CardTitle>
              <CardDescription>
                Geographic distribution of visitors.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {countriesData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={countriesData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                      >
                        {countriesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2">
                    {countriesData.map((country, index) => (
                      <div key={country.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                          <span className="text-sm text-foreground">{country.name}</span>
                        </div>
                        <span className="text-sm font-medium text-muted-foreground">
                          {country.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No country data available. (IP lookup is disabled for GDPR)
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Analytics;
